import React, { useEffect, useState } from 'react';
import { fetchLatestData, postNewData } from '../../redux/action/customFile';
import { useHistory } from 'react-router-dom';
import PageLoader from '../../shared/PageLoader';
import './Agent.css';

const initialsOf = (name) => {
  if (!name) return 'A';
  return name.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('');
};

export default function AgentList() {
  const [results, setResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  const getResults = () => {
    setLoading(true);
    fetchLatestData(
      "agents/get/all",
      {},
      (data) => {
        setResults(Array.isArray(data) ? data : []);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
        setMessage({ type: 'error', text: 'Unable to load agents.' });
      }
    );
  };

  useEffect(() => {
    getResults();
  }, []);

  const lowercasedQuery = searchQuery.toLowerCase();
  const filteredResults = (results || []).filter((item) =>
    (item.agentName || '').toLowerCase().includes(lowercasedQuery) ||
    String(item.phoneNumber || '').includes(lowercasedQuery)
  );

  const toCount = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const totalPassengers = filteredResults.reduce((sum, item) => sum + toCount(item.voucherCount), 0);
  const allPassengers = results.reduce((sum, item) => sum + toCount(item.voucherCount), 0);
  const topAgents = [...results]
    .sort((a, b) => toCount(b.voucherCount) - toCount(a.voucherCount))
    .slice(0, 5)
    .filter((item) => toCount(item.voucherCount) > 0);
  const topPax = toCount(topAgents[0] && topAgents[0].voucherCount);

  const handleDelete = (id) => {
    if (!window.confirm('Remove this agent from the directory?')) return;
    postNewData(
      { id: id },
      "delete/agent/id",
      (data) => {
        setMessage({ type: 'success', text: data.message || 'Agent removed.' });
        getResults();
      },
      (err) => {
        console.log(err);
        setMessage({ type: 'error', text: 'Unable to delete this agent.' });
      }
    );
  };

  return (
    <div className="ag-page">
      <div className="ag-header">
        <div>
          <h3>Agents</h3>
          <p>Directory of booking agents and their passenger volume.</p>
        </div>
        <div className="ag-header-actions">
          <span className="ag-count">{loading ? 'Loading…' : `${filteredResults.length} agent${filteredResults.length === 1 ? '' : 's'}`}</span>
          <button type="button" className="ag-create" onClick={() => history.push('/agent/new/form')}>
            <span className="mdi mdi-plus" /> New agent
          </button>
        </div>
      </div>

      {message.text && <div className={`ag-banner ${message.type}`}>{message.text}</div>}

      {loading ? (
        <PageLoader title="Loading agents" message="Please wait while we fetch the agent directory." />
      ) : (
      <>
      {topAgents.length > 0 && (
        <div className="ag-top">
          <div className="ag-top-head">
            <h4>Top 5 agents by pax</h4>
            <span>{allPassengers} passengers overall</span>
          </div>
          <div className="ag-top-grid">
            {topAgents.map((item, index) => {
              const pax = toCount(item.voucherCount);
              const width = topPax ? Math.max(8, Math.round((pax / topPax) * 100)) : 0;
              return (
                <button
                  type="button"
                  className="ag-top-card"
                  key={item.id || item.agentName || index}
                  onClick={() => setSearchQuery(item.agentName || '')}
                  title="Show this agent in the list"
                >
                  <div className="ag-top-rank">{index + 1}</div>
                  <div className="ag-top-meta">
                    <div className="ag-name">{item.agentName || '—'}</div>
                    <div className="ag-top-pax">{pax} pax</div>
                    <div className="ag-top-bar">
                      <span style={{ width: `${width}%` }} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="ag-card">
        <div className="ag-toolbar">
          <div className="ag-search">
            <span className="mdi mdi-magnify" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by agent name or phone"
            />
          </div>
          <span className="ag-count">{totalPassengers} passenger{totalPassengers === 1 ? '' : 's'}</span>
        </div>

        <div className="ag-table-wrap">
          <table className="ag-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Agent</th>
                <th>Phone</th>
                <th>Passengers</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.map((item, i) => (
                <tr key={item.id || i}>
                  <td>{i + 1}</td>
                  <td>
                    <div className="ag-person">
                      <div className="ag-avatar">{initialsOf(item.agentName)}</div>
                      <div className="ag-name">{item.agentName || '—'}</div>
                    </div>
                  </td>
                  <td>{item.phoneNumber || '—'}</td>
                  <td>{toCount(item.voucherCount)}</td>
                  <td>
                    <button type="button" className="ag-del" title="Delete agent" onClick={() => handleDelete(item.id)}>
                      <span className="mdi mdi-delete-outline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filteredResults.length && (
            <div className="ag-empty">
              <span className="mdi mdi-account-tie" style={{ fontSize: '1.7rem' }} />
              <h4>No agents found</h4>
              <p>Try a different search or create a new agent.</p>
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
